import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAPI } from '@/hooks/useAPI'
import { getPartners } from '@/services/api/partners'
import PartnerList from '@/components/business/Partner/PartnerList'

const Partners = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const pageSize = 12

  // 直接使用通用Hook获取数据
  const { data: partnersData, loading, error, refetch } = useAPI(
    () => getPartners({ page, pageSize }),
    [page, i18n.language]
  )

  const partners = partnersData?.records || []
  const pagination = {
    total: partnersData?.total || 0,
    page: partnersData?.current || 1,
    totalPages: partnersData?.pages || 1,
    hasPrevious: (partnersData?.current || 1) > 1,
    hasNext: (partnersData?.current || 1) < (partnersData?.pages || 1)
  }

  // 处理分页变化
  const handlePageChange = (newPage) => {
    setPage(newPage)
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // 处理合作伙伴点击
  const handlePartnerClick = (partner) => {
    navigate(`/partners/${partner.orgId}`)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('partners.title')}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            {t('partners.subtitle')}
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8 max-w-7xl">

        {/* 统计信息 */}
        {!loading && !error && pagination.total > 0 && (
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">
                {pagination.total || 0}
              </span>
              {t('partners.stats.totalPartners')}
            </div>
            {pagination.totalPages > 1 && (
              <div className="text-sm text-gray-600">
                {t('partners.stats.pageInfo', { 
                  current: pagination.page, 
                  total: pagination.totalPages 
                })}
              </div>
            )}
          </div>
        )}

        {/* 合作伙伴列表 */}
        <PartnerList 
          partners={partners}
          loading={loading}
          error={error}
          onPartnerClick={handlePartnerClick}
        />

        {/* 分页组件 */}
        {!loading && !error && pagination.totalPages > 1 && (
          <div className="flex justify-center">
            <nav className="flex items-center space-x-2">
              {/* 上一页按钮 */}
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={!pagination.hasPrevious}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  pagination.hasPrevious
                    ? 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {t('partners.pagination.previous')}
              </button>

              {/* 页码按钮 */}
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter(pageNum => 
                  pageNum === 1 || 
                  pageNum === pagination.totalPages || 
                  Math.abs(pageNum - page) <= 2
                )
                .map((pageNum, index, array) => {
                  // 添加省略号
                  const showEllipsis = index > 0 && pageNum - array[index - 1] > 1
                  
                  return (
                    <div key={pageNum} className="flex items-center">
                      {showEllipsis && (
                        <span className="px-2 py-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          pageNum === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    </div>
                  )
                })}

              {/* 下一页按钮 */}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={!pagination.hasNext}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  pagination.hasNext
                    ? 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {t('partners.pagination.next')}
              </button>
            </nav>
          </div>
        )}

        {/* 重新加载按钮 (错误状态时显示) */}
        {error && (
          <div className="text-center mt-8">
            <button
              onClick={() => refetch()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md 
                         transition-colors font-medium"
            >
              {t('partners.states.retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Partners 